/**
 * Limpeza da sessão do chat de atendimento.
 *
 * Isolado em um módulo sem dependências porque o interceptor do axios também
 * precisa dele, ao derrubar uma sessão expirada — e chatSuporte.js importa o
 * próprio axios, o que fecharia um ciclo.
 */

/**
 * Chaves em que o widget guarda a conversa em andamento.
 *
 * Ele grava em localStorage (com cookie como alternativa), e nada disso é
 * apagado ao trocar de usuário no Espião Cloud. Sem esta limpeza, quem entrasse
 * em seguida no mesmo computador abriria o chat e veria a conversa do usuário
 * anterior.
 */
const CHAVES_DA_CONVERSA = ['com_tomticket_chat_hash', 'com_tomticket_x_csrf_token'];

/** Elementos que o widget monta no body, mais a tag do script. */
const ELEMENTOS_DO_WIDGET = [
  'comTomTicketChatWidget',
  'comTomTicketChatContainer',
  'comTomTicketChatFrame',
  'tomticket-chat-script',
];

/**
 * Encerra o chat e apaga o rastro da conversa. Chamado ao sair do sistema,
 * seja pelo logout ou por sessão expirada.
 */
/**
 * De quem é a conversa guardada. Fica no localStorage junto com as chaves do
 * widget, porque é lá que elas vivem.
 */
const CHAVE_DONO = 'chat_suporte_dono';

/**
 * Descarta a conversa quando ela pertence a outro usuário.
 *
 * O widget guarda a conversa no localStorage, que é compartilhado por todas as
 * abas do navegador, enquanto a nossa sessão é por aba. Sem esta checagem, dois
 * usuários logados em abas diferentes — ou em sequência, sem logout — veem a
 * conversa um do outro.
 *
 * @param {string} dono Identificador do vínculo atual, no formato conta-usuário
 * @return {boolean} true quando havia conversa de outro e ela foi descartada
 */
export const garantirDonoDoChat = (dono) => {
  if (!dono) return false;

  let anterior = null;
  try {
    anterior = window.localStorage.getItem(CHAVE_DONO);
  } catch {
    return false;
  }

  const trocou = anterior !== null && anterior !== dono;

  if (trocou) encerrarChatSuporte();

  try {
    window.localStorage.setItem(CHAVE_DONO, dono);
  } catch {
    /* localStorage bloqueado: o widget também não vai conseguir guardar nada */
  }

  return trocou;
};

export const encerrarChatSuporte = () => {
  CHAVES_DA_CONVERSA.forEach((chave) => {
    // A rotina do próprio widget apaga dos dois lugares onde ele grava.
    try {
      window.comTTChat?.core?.deleteCookie?.(chave);
    } catch {
      /* segue para a limpeza manual */
    }

    try {
      window.localStorage.removeItem(chave);
    } catch {
      /* localStorage pode estar bloqueado */
    }

    try {
      document.cookie = `${chave}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${chave}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    } catch {
      /* idem */
    }
  });

  ELEMENTOS_DO_WIDGET.forEach((id) => {
    try {
      document.querySelectorAll(`#${id}`).forEach((el) => el.remove());
    } catch {
      /* elemento já removido */
    }
  });

  try {
    window.localStorage.removeItem(CHAVE_DONO);
  } catch {
    /* idem */
  }

  try {
    delete window.comTTChat;
  } catch {
    window.comTTChat = undefined;
  }
};

export default encerrarChatSuporte;
